// Grid-wide helpers for the Closer events grid: the miles total shown under the grid, and
// the "Apply Suggested Locations" bulk action.

// Totals the per-row Mi and G-Mi values the grid shows (eventDriveTimeMiles / driveTimeMiles),
// skipping deactivated rows the same way CloserSummary does. No new mileage maths here.
export const sumEventMiles = (events: any[]) =>
  events
    .filter((event) => event.isActive !== false)
    .reduce(
      (totals, event) => ({
        miles: totals.miles + (Number(event.eventDriveTimeMiles) || 0),
        gpsMiles: totals.gpsMiles + (Number(event.driveTimeMiles) || 0),
      }),
      { miles: 0, gpsMiles: 0 }
    );

// Same rule the server applies on a trip's first load (OrderApi EventsRepo
// AutoApplyGpsToTripEventsAsync, Rule 2): a positive recommendedPointOfServiceId that differs
// from the row's Route Location, on any row except End Trip, whose location AutoFix owns.
// The client additionally requires the id to be one the Route Location dropdown offers
// (isSelectable): an id outside that list renders as a blank Route Location, which would
// wipe the location the row already had.
export const hasApplicableSuggestion = (
  event: any,
  isEndTrip: (event: any) => boolean,
  isSelectable: (id: number) => boolean = () => true
) => {
  const suggestion = Number(event?.recommendedPointOfServiceId);
  if (!(suggestion > 0)) return false;
  if (isEndTrip(event)) return false;
  if (!isSelectable(suggestion)) return false;
  return String(event.routeLocation ?? "") !== String(suggestion);
};

// Rows that have a suggestion id the button would apply but for it not being in the Route
// Location dropdown. Counted so the closer is told why those rows were left alone.
export const isUnselectableSuggestion = (
  event: any,
  isEndTrip: (event: any) => boolean,
  isSelectable: (id: number) => boolean
) => {
  const suggestion = Number(event?.recommendedPointOfServiceId);
  if (!(suggestion > 0) || isEndTrip(event) || isSelectable(suggestion)) return false;
  return String(event.routeLocation ?? "") !== String(suggestion);
};

// Applies every row's suggestion in grid order through the same setter a hand pick uses, so
// paired-event propagation behaves exactly as if the closer picked each one in turn. Each
// step sees the result of the previous one.
export const applySuggestedLocations = (
  events: any[],
  isEndTrip: (event: any) => boolean,
  setRouteLocation: (events: any[], eventId: any, value: string) => any[],
  isSelectable: (id: number) => boolean = () => true
) => {
  let appliedCount = 0;
  const updated = events.reduce((current, original) => {
    const row = current.find((event) => event.id === original.id);
    if (!hasApplicableSuggestion(row, isEndTrip, isSelectable)) return current;
    appliedCount++;
    return setRouteLocation(current, row.id, String(Number(row.recommendedPointOfServiceId)));
  }, events);
  return { events: updated, appliedCount };
};
